/**
 * SidebarStep4_FutureClosure - Projection Settings (Future Closure Path)
 * 
 * This component provides projection settings for the Future Forced Closure calculation.
 * Shows when calculation_mode === 'future_closure'
 * 
 * Features:
 *   - Aggression Slider
 *   - Base Period Selection (Last 36 months vs Best 36 months)
 *   - Closure Duration
 *   - Live preview of total demand
 *   - P&L Data Source Indicator (P1 Feature)
 *   - Continuation Rate Editor link (P2 Feature)
 * 
 * Preserves ALL functionality from Step4_FutureClosure.tsx - only layout changes.
 */

import React, { useState, useEffect } from 'react';
import { 
  AlertTriangle, 
  Calculator, 
  Info, 
  Loader2, 
  TrendingUp, 
  Clock,
  DollarSign,
  Target,
  Calendar,
  Sliders,
  Settings2
} from 'lucide-react';
import WizardV2Layout from './WizardV2Layout';
import ContinuationRateEditor from '../../ContinuationRateEditor';
import { useAuth } from '../../../contexts/TestAuthContext';
import { 
  FutureClosureCalculationParams, 
  CaseConfig,
  calculateFutureClosure,
  getCaseConfig,
  saveCaseConfig,
  setTokenGetter
} from '../../../services/futureClosureApi';

interface SidebarStep4FutureClosureProps {
  onNext: (data: any) => void;
  onBack: () => void;
  data: any;
  onDataUpdate?: (data: any) => void;
  onSave?: (explicitData?: any) => Promise<void>;
  isSaving?: boolean;
  lastSaved?: Date | null;
  goToStep?: (step: number) => void;
  highestCompletedStep?: number;
  claimId?: string;
}

export default function SidebarStep4_FutureClosure({ 
  onNext, 
  onBack, 
  data, 
  onDataUpdate, 
  onSave,
  isSaving,
  lastSaved,
  goToStep,
  highestCompletedStep,
  claimId
}: SidebarStep4FutureClosureProps) {
  const { getToken } = useAuth();
  
  // Form state
  const [aggression, setAggression] = useState(data.future_closure_params?.aggression ?? 50);
  const [useBestBase, setUseBestBase] = useState(data.future_closure_params?.use_best_base ?? false);
  const [baseMonths, setBaseMonths] = useState(data.future_closure_params?.base_months ?? 36);
  // Closure duration comes from Step 2 (Event Details) - read-only here
  const closureDuration = data.closure_duration_months || data.future_closure_params?.closure_duration_months || 36;
  
  // Result state
  const [loading, setLoading] = useState(false);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any>(data.future_closure_result || null);
  const [caseConfig, setCaseConfig] = useState<CaseConfig | null>(null);
  const [needsRecalc, setNeedsRecalc] = useState(false);
  
  // P&L status state (P1: Future Closure UI Integration)
  const [plStatus, setPlStatus] = useState<{
    pl_source: 'provided' | 'reconstructed' | 'none';
    has_provided_pl: boolean;
    has_transactions: boolean;
    pl_files: Array<{id: string; filename: string}>;
  } | null>(null);
  
  // Continuation Rate Editor state (P2 Feature)
  const [showRateEditor, setShowRateEditor] = useState(false);
  
  // Baseline for percentage comparison (calculated at aggression=0)
  const [baseline, setBaseline] = useState<{
    lostProfits: number;
    continuingExpenses: number;
    oneTimeCosts: number;
    totalDemand: number;
  } | null>(null);

  // Set up the token getter for API calls
  useEffect(() => {
    setTokenGetter(getToken);
  }, [getToken]);

  // Load P&L status to show data source indicator (P1: Future Closure UI Integration)
  useEffect(() => {
    const loadPlStatus = async () => {
      if (!data.id) return;
      
      try {
        const token = await getToken();
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const response = await fetch(`${backendUrl}/api/v1/document-types/pl-status/${data.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.ok) {
          const status = await response.json();
          setPlStatus(status);
        }
      } catch (err) {
        console.log('Could not load P&L status:', err);
      }
    };
    
    loadPlStatus();
  }, [data.id, getToken]);

  // Load existing case configuration on mount
  useEffect(() => {
    const loadConfig = async () => {
      if (!data.id) {
        setLoadingConfig(false);
        return;
      }

      try {
        const config = await getCaseConfig(data.id);
        setCaseConfig(config);
        if (config) {
          if (config.aggression !== undefined) setAggression(config.aggression);
          if (config.use_best_base !== undefined) setUseBestBase(config.use_best_base);
          if (config.base_months !== undefined) setBaseMonths(config.base_months);
          // Note: closure_duration_months now comes from Step 2 (data.closure_duration_months)
        }
      } catch (err) {
        console.log('No existing config found, using defaults');
      } finally {
        setLoadingConfig(false);
      }
    };

    loadConfig();
  }, [data.id]);

  // Auto-recalculate when aggression changes (debounced)
  // Note: This triggers a re-calculation via the backend, not mock
  useEffect(() => {
    if (!result || !data.id) return; // Only auto-recalc if we have an existing result and case ID
    
    const timer = setTimeout(async () => {
      const params: FutureClosureCalculationParams = {
        aggression,
        use_best_base: useBestBase,
        base_months: baseMonths,
        closure_duration_months: closureDuration,
      };
      try {
        console.log('📊 Auto-recalculating with new aggression:', aggression);
        const newResult = await calculateFutureClosure(data.id, params);
        setResult(newResult);
      } catch (err) {
        console.error('Auto-recalculation failed:', err);
        // Keep existing result on error, don't fallback to mock
      }
    }, 500); // 500ms debounce for API calls
    
    return () => clearTimeout(timer);
  }, [aggression, data.id]); // Trigger on aggression change

  // Note: generateMockResult is kept only for reference/documentation but NOT used
  // All calculations must go through the backend engine
  const generateMockResult = (params: FutureClosureCalculationParams) => {
    console.warn('⚠️ generateMockResult called but should not be used - use backend engine');
    const aggressionVal = params.aggression ?? 50;
    const closureDur = params.closure_duration_months ?? 36;
    const useBest = params.use_best_base ?? false;
    
    // Get financial data by year
    let yearlyRevenues: Record<number, number> = {};
    let annualRevenue = 1000000; // Default
    
    if (data.financial_data) {
      const yearKeys = Object.keys(data.financial_data).filter(k => k.startsWith('data_'));
      yearKeys.forEach(key => {
        const year = parseInt(key.replace('data_', ''));
        const yearData = data.financial_data[key];
        
        // Handle two different data formats:
        // Format 1 (from templates): { revenue: [month1, month2, ...], cogs: [...] }
        // Format 2 (from uploaded files): [{ revenue: x, cogs: y }, { revenue: x2, cogs: y2 }, ...]
        
        if (yearData && typeof yearData === 'object') {
          if (Array.isArray(yearData)) {
            // Format 2: Array of monthly objects
            yearlyRevenues[year] = yearData.reduce((sum: number, m: any) => sum + (m.revenue || 0), 0);
          } else if (Array.isArray(yearData.revenue)) {
            // Format 1: Object with revenue array
            yearlyRevenues[year] = yearData.revenue.reduce((sum: number, val: number) => sum + (val || 0), 0);
          } else if (typeof yearData.revenue === 'number') {
            // Format 3: Object with single revenue value (annual)
            yearlyRevenues[year] = yearData.revenue;
          }
        }
      });
      
      console.log('📊 Step 4 - yearlyRevenues extracted:', yearlyRevenues);
      
      // Calculate base period revenue based on selection
      const sortedYears = Object.keys(yearlyRevenues).map(Number).sort((a, b) => a - b);
      
      if (sortedYears.length >= 3) {
        if (useBest) {
          // Find best 3-year window
          let bestRevenue = 0;
          for (let i = 0; i <= sortedYears.length - 3; i++) {
            const windowYears = sortedYears.slice(i, i + 3);
            const windowRevenue = windowYears.reduce((sum, y) => sum + (yearlyRevenues[y] || 0), 0);
            if (windowRevenue > bestRevenue) {
              bestRevenue = windowRevenue;
            }
          }
          annualRevenue = bestRevenue / 3; // Average annual from best period
        } else {
          // Use last 3 years
          const last3 = sortedYears.slice(-3);
          const last3Revenue = last3.reduce((sum, y) => sum + (yearlyRevenues[y] || 0), 0);
          annualRevenue = last3Revenue / 3; // Average annual from last period
        }
      } else if (sortedYears.length > 0) {
        // Use all available data
        const totalRev = Object.values(yearlyRevenues).reduce((a, b) => a + b, 0);
        annualRevenue = totalRev / sortedYears.length;
      }
    }
    
    // Aggression affects both growth projection AND expense assumptions
    // 0 = very conservative (70% of base), 100 = aggressive (130% of base)
    const aggressionMultiplier = 0.7 + (aggressionVal / 100) * 0.6;
    
    // Also affects gross margin assumptions
    const grossMargin = 0.55 + (aggressionVal / 100) * 0.15; // 55% to 70%
    
    // And expense continuation rates
    const expenseContinuationRate = 0.10 + (aggressionVal / 100) * 0.10; // 10% to 20% of revenue
    
    const lostRevenue = annualRevenue * (closureDur / 12) * aggressionMultiplier;
    const lostGrossProfit = lostRevenue * grossMargin;
    const continuingExpenses = annualRevenue * expenseContinuationRate * (closureDur / 12);
    
    // One-time costs also scale with aggression
    const shutdownCost = annualRevenue * (0.03 + (aggressionVal / 100) * 0.04); // 3% to 7%
    const restartCost = annualRevenue * (0.05 + (aggressionVal / 100) * 0.10);  // 5% to 15%
    
    const totalLostProfits = lostGrossProfit - continuingExpenses;
    
    return {
      success: true,
      parameters: params,
      pro_forma_summary: {
        total_lost_profits: Math.round(totalLostProfits),
        total_lost_revenue: Math.round(lostRevenue),
        total_continuing_expenses: Math.round(continuingExpenses),
        gross_profit_margin: grossMargin,
        closure_months: closureDur,
        base_period_type: useBest ? 'Best 36 Months' : 'Last 36 Months',
        aggression_multiplier: aggressionMultiplier,
      },
      one_time_costs: {
        shutdown: Math.round(shutdownCost),
        restart: Math.round(restartCost),
        total: Math.round(shutdownCost + restartCost),
      },
      total_demand: Math.round(totalLostProfits + shutdownCost + restartCost),
    };
  };

  // Calculate percentage change from baseline
  const calcPercentChange = (current: number, base: number): { pct: number; formatted: string } => {
    if (base === 0) return { pct: 0, formatted: '+0%' };
    const pct = ((current - base) / base) * 100;
    const formatted = pct >= 0 ? `+${Math.round(pct)}%` : `${Math.round(pct)}%`;
    return { pct, formatted };
  };

  // Calculate future closure demand
  const handleCalculate = async () => {
    setLoading(true);
    setError(null);

    const params: FutureClosureCalculationParams = {
      aggression,
      use_best_base: useBestBase,
      base_months: baseMonths,
      closure_duration_months: closureDuration,
    };

    try {
      if (!data.id) {
        setError('No case ID available - please save the case first');
        setLoading(false);
        return;
      }

      // Always use the real calculation engine - no mock fallback
      console.log('📊 Calling backend calculation engine with params:', params);
      const calcResult = await calculateFutureClosure(data.id, params);
      console.log('📊 Backend calculation result:', calcResult);
      setResult(calcResult);
      
      // Calculate baseline at aggression=0 for percentage comparison using the real engine
      const baselineParams = { ...params, aggression: 0 };
      try {
        const baselineResult = await calculateFutureClosure(data.id, baselineParams);
        setBaseline({
          lostProfits: baselineResult.pro_forma_summary?.total_lost_profits || 0,
          continuingExpenses: baselineResult.pro_forma_summary?.total_continuing_expenses || 0,
          oneTimeCosts: baselineResult.one_time_costs?.total || 0,
          totalDemand: baselineResult.total_demand || 0,
        });
      } catch (baselineErr) {
        console.log('Baseline calculation skipped:', baselineErr);
      }

      // Save configuration
      const configToSave: CaseConfig = {
        aggression,
        use_best_base: useBestBase,
        base_months: baseMonths,
        closure_duration_months: closureDuration,
      };
      await saveCaseConfig(data.id, configToSave);
      setCaseConfig(configToSave);
    } catch (err: any) {
      console.error('Future closure calculation error:', err);
      setError(err.message || 'Failed to calculate future closure. Please check the case data.');
      // DO NOT fall back to mock - show error instead
    } finally {
      setLoading(false);
    }
  };

  // Handle next
  const handleNext = () => {
    if (!result) {
      setError('Please calculate the projection before continuing');
      return;
    }

    onNext({
      future_closure_result: result,
      future_closure_params: {
        aggression,
        use_best_base: useBestBase,
        base_months: baseMonths,
        closure_duration_months: closureDuration,
      },
      future_closure_config: caseConfig,
    });
  };

  // Aggression label and color
  const getAggressionLabel = () => {
    if (aggression < 30) return 'Conservative';
    if (aggression < 70) return 'Moderate';
    return 'Aggressive';
  };

  const getAggressionColor = () => {
    if (aggression < 30) return 'text-blue-600';
    if (aggression < 70) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Calculate completion
  const completionPercent = 75;
  const grade = result ? 'B' : 'C';

  // Build sidebar sections - reflects new layout order
  const buildSections = () => [
    {
      id: 'base_period',
      label: 'Base Period',
      isActive: true,
      isComplete: true,
      onClick: () => document.getElementById('base-period-section')?.scrollIntoView({ behavior: 'smooth' })
    },
    {
      id: 'closure_duration',
      label: 'Closure Duration',
      isActive: false,
      isComplete: true,
      onClick: () => document.getElementById('closure-duration-section')?.scrollIntoView({ behavior: 'smooth' })
    },
    {
      id: 'results',
      label: 'Results Preview',
      isActive: false,
      isComplete: !!result,
      onClick: () => document.getElementById('results-section')?.scrollIntoView({ behavior: 'smooth' })
    },
    {
      id: 'aggression',
      label: 'Fine-Tune (Aggression)',
      isActive: false,
      isComplete: !!result,
      onClick: () => document.getElementById('aggression-section')?.scrollIntoView({ behavior: 'smooth' })
    }
  ];

  // Build QA items
  const buildQAItems = () => {
    const items: Array<{id: string; label: string; status: 'missing' | 'warning' | 'ok'}> = [];
    
    items.push({ id: 'base', label: 'Base period selected', status: 'ok' });
    
    if (result) {
      items.push({ id: 'calculated', label: 'Projection calculated', status: 'ok' });
    } else {
      items.push({ id: 'calculated', label: 'Run calculation', status: 'warning' });
    }
    
    return items;
  };

  if (loadingConfig) {
    return (
      <WizardV2Layout
        currentStep={4}
        highestCompletedStep={highestCompletedStep}
        stepTitle="Projection Settings"
        stepSubtitle="Loading configuration..."
        completionPercent={75}
        calculationMode="future_closure"
        grade="C"
        sections={[]}
        qaItems={[]}
        goToStep={goToStep}
        onBack={onBack}
        onNext={() => {}}
        showNext={false}
        claimId={claimId}
      >
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            <p className="text-gray-600">Loading configuration...</p>
          </div>
        </div>
      </WizardV2Layout>
    );
  }

  return (
    <WizardV2Layout
      currentStep={4}
      highestCompletedStep={highestCompletedStep}
      stepTitle="Projection Settings"
      stepSubtitle="Configure pro forma projection for forced closure"
      completionPercent={completionPercent}
      grade={grade}
      calculationMode="future_closure"
      sections={buildSections()}
      qaItems={buildQAItems()}
      nextAction={!result ? {
        label: 'Calculate projection',
        description: 'Required before continuing'
      } : undefined}
      isSaving={isSaving}
      lastSaved={lastSaved}
      onSave={onSave}
      goToStep={goToStep}
      onBack={onBack}
      onNext={handleNext}
      claimId={claimId}
      nextLabel="Continue to Expense Continuation"
      isNextDisabled={!result}
      showNext={true}
    >
      <div className="p-6 lg:p-8 space-y-6">
        
        {/* Info Banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="text-sm text-amber-800 font-medium">
                Pro Forma Calculation
              </p>
              <p className="text-sm text-amber-700 mt-1">
                Projects business losses over a forced closure period 
                (toxic contamination, PFAS remediation, etc.)
              </p>
            </div>
          </div>
        </div>

        {/* P&L Data Source Indicator (P1: Future Closure UI Integration) */}
        {plStatus && (
          <div className={`rounded-lg p-4 border ${
            plStatus.pl_source === 'provided' 
              ? 'bg-green-50 border-green-200' 
              : plStatus.pl_source === 'reconstructed'
              ? 'bg-blue-50 border-blue-200'
              : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="flex items-start gap-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                plStatus.pl_source === 'provided' 
                  ? 'bg-green-100' 
                  : plStatus.pl_source === 'reconstructed'
                  ? 'bg-blue-100'
                  : 'bg-gray-100'
              }`}>
                {plStatus.pl_source === 'provided' ? (
                  <DollarSign className="w-4 h-4 text-green-600" />
                ) : plStatus.pl_source === 'reconstructed' ? (
                  <Calculator className="w-4 h-4 text-blue-600" />
                ) : (
                  <Info className="w-4 h-4 text-gray-400" />
                )}
              </div>
              <div className="flex-1">
                <p className={`text-sm font-medium ${
                  plStatus.pl_source === 'provided' 
                    ? 'text-green-800' 
                    : plStatus.pl_source === 'reconstructed'
                    ? 'text-blue-800'
                    : 'text-gray-600'
                }`}>
                  {plStatus.pl_source === 'provided' 
                    ? '✓ Using Uploaded P&L Statement' 
                    : plStatus.pl_source === 'reconstructed'
                    ? '✓ Using Reconstructed P&L (from transactions)'
                    : 'No P&L Data Available'}
                </p>
                <p className={`text-xs mt-1 ${
                  plStatus.pl_source === 'provided' 
                    ? 'text-green-700' 
                    : plStatus.pl_source === 'reconstructed'
                    ? 'text-blue-700'
                    : 'text-gray-500'
                }`}>
                  {plStatus.pl_source === 'provided' && plStatus.pl_files.length > 0
                    ? `Source: ${plStatus.pl_files.map(f => f.filename).join(', ')}`
                    : plStatus.pl_source === 'reconstructed'
                    ? 'Financial data extracted from uploaded bank statements'
                    : 'Upload a P&L statement or bank statements to enable calculations'}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Base Period Selection Card */}
        <div id="base-period-section" className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900">Base Period Selection</h3>
          </div>

          <div className="space-y-4">
            <div className="flex gap-4">
              {(() => {
                // Calculate the actual periods that would be used
                // First try data.calculated_years, then extract from financial_data keys
                let years = data.calculated_years || [];
                
                // If no calculated_years, extract from financial_data keys
                if (years.length === 0 && data.financial_data) {
                  const yearKeys = Object.keys(data.financial_data).filter(k => k.startsWith('data_'));
                  years = yearKeys.map(k => parseInt(k.replace('data_', ''))).filter(y => !isNaN(y)).sort((a, b) => a - b);
                }
                
                const sortedYears = [...years].sort((a: number, b: number) => b - a);
                const last3Years = sortedYears.slice(0, 3);
                
                const financialData = data.financial_data || {};
                let bestPeriodYears: number[] = [];
                let bestPeriodRevenue = 0;
                
                // Helper function to extract revenue from year data (handles both formats)
                const getYearRevenue = (yearData: any): number => {
                  if (!yearData) return 0;
                  if (Array.isArray(yearData)) {
                    // Format 2: Array of monthly objects [{revenue: x}, ...]
                    return yearData.reduce((sum: number, m: any) => sum + (m.revenue || 0), 0);
                  } else if (Array.isArray(yearData.revenue)) {
                    // Format 1: Object with revenue array {revenue: [m1, m2, ...]}
                    return yearData.revenue.reduce((sum: number, val: number) => sum + (val || 0), 0);
                  } else if (typeof yearData.revenue === 'number') {
                    // Format 3: Object with single revenue value
                    return yearData.revenue;
                  }
                  return 0;
                };
                
                if (years.length >= 3) {
                  const sortedAsc = [...years].sort((a: number, b: number) => a - b);
                  for (let i = 0; i <= sortedAsc.length - 3; i++) {
                    const windowYears = sortedAsc.slice(i, i + 3);
                    let windowRevenue = 0;
                    windowYears.forEach((year: number) => {
                      const yearKey = `data_${year}`;
                      const yearData = financialData[yearKey];
                      windowRevenue += getYearRevenue(yearData);
                    });
                    if (windowRevenue > bestPeriodRevenue) {
                      bestPeriodRevenue = windowRevenue;
                      bestPeriodYears = windowYears;
                    }
                  }
                } else if (years.length > 0) {
                  // Less than 3 years, use all available
                  bestPeriodYears = [...years].sort((a, b) => a - b);
                }
                
                const last3Display = last3Years.length > 0 
                  ? `${Math.min(...last3Years)} - ${Math.max(...last3Years)}`
                  : 'N/A';
                  
                const best3Display = bestPeriodYears.length > 0 
                  ? `${Math.min(...bestPeriodYears)} - ${Math.max(...bestPeriodYears)}`
                  : last3Display;
                  
                const isSamePeriod = last3Display === best3Display;

                return (
                  <>
                    <button
                      type="button"
                      onClick={() => setUseBestBase(false)}
                      className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                        !useBestBase 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-4 h-4 rounded-full border-2 ${!useBestBase ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}>
                          {!useBestBase && <div className="w-2 h-2 bg-white rounded-full m-0.5" />}
                        </div>
                        <span className="font-medium text-gray-900">Last 36 Months</span>
                      </div>
                      <p className="text-sm text-gray-600 text-left mb-2">Use the most recent 36 months of data</p>
                      <p className="text-xs font-semibold text-blue-700 bg-blue-100 px-2 py-1 rounded inline-block">
                        → {last3Display}
                      </p>
                    </button>

                    <button
                      type="button"
                      onClick={() => setUseBestBase(true)}
                      className={`flex-1 p-4 rounded-lg border-2 transition-all ${
                        useBestBase 
                          ? 'border-blue-500 bg-blue-50' 
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div className={`w-4 h-4 rounded-full border-2 ${useBestBase ? 'border-blue-500 bg-blue-500' : 'border-gray-300'}`}>
                          {useBestBase && <div className="w-2 h-2 bg-white rounded-full m-0.5" />}
                        </div>
                        <span className="font-medium text-gray-900">Best 36 Months</span>
                      </div>
                      <p className="text-sm text-gray-600 text-left mb-2">Automatically select highest-performing period</p>
                      <p className="text-xs font-semibold text-green-700 bg-green-100 px-2 py-1 rounded inline-block">
                        → {best3Display} {!isSamePeriod && '✓ Higher revenue'}
                      </p>
                    </button>
                  </>
                );
              })()}
            </div>

            {/* Show available data info */}
            {(() => {
              let availableYears = data.calculated_years || [];
              if (availableYears.length === 0 && data.financial_data) {
                const yearKeys = Object.keys(data.financial_data).filter(k => k.startsWith('data_'));
                availableYears = yearKeys.map(k => parseInt(k.replace('data_', ''))).filter(y => !isNaN(y)).sort((a, b) => a - b);
              }
              
              if (availableYears.length > 0) {
                return (
                  <p className="text-sm text-gray-500">
                    Available data: {availableYears.join(', ')} ({availableYears.length} years)
                    {data.fromTemplate && <span className="ml-2 text-blue-600">(from template)</span>}
                  </p>
                );
              } else {
                return (
                  <p className="text-sm text-amber-600">
                    No financial data available. Please go back and upload P&L data or apply a template.
                  </p>
                );
              }
            })()}
          </div>
        </div>

        {/* Closure Duration Card - READ-ONLY display (set in Step 2) */}
        <div id="closure-duration-section" className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Closure Duration</h3>
              <p className="text-sm text-gray-500">Set in Event Details (Step 2)</p>
            </div>
          </div>

          <div className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-lg">
            <span className="text-gray-700 font-medium">Projected closure period</span>
            <span className="text-lg font-semibold text-gray-900">
              {closureDuration} months ({Math.round(closureDuration / 12)} {closureDuration / 12 === 1 ? 'year' : 'years'})
            </span>
          </div>
          <p className="text-xs text-gray-500 mt-2 text-center">
            To change, go back to Step 2 - Event Details
          </p>
        </div>

        {/* Continuation Rate Editor Card */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Sliders className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Continuation Rates</h3>
                <p className="text-sm text-gray-500">Customize expense continuation percentages</p>
              </div>
            </div>
            <button
              onClick={() => setShowRateEditor(!showRateEditor)}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors text-sm font-medium text-gray-700"
            >
              <Settings2 className="w-4 h-4" />
              {showRateEditor ? 'Hide Editor' : 'Edit Rates'}
            </button>
          </div>
          
          {showRateEditor && (
            <div className="mt-6 pt-6 border-t border-gray-100">
              <ContinuationRateEditor
                reportId={data.id}
                onSave={() => {
                  // Optionally trigger recalculation
                  if (result) {
                    setNeedsRecalc(true);
                  }
                }}
              />
            </div>
          )}
        </div>

        {/* Calculate Button */}
        <div className="flex justify-center">
          <button
            onClick={() => {
              console.log('🔵 Calculate button clicked!');
              handleCalculate();
            }}
            disabled={loading}
            data-testid="calculate-projection-btn"
            className="px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transition-all disabled:opacity-50 flex items-center gap-3"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Calculating...
              </>
            ) : (
              <>
                <Calculator className="w-5 h-5" />
                Calculate Projection
              </>
            )}
          </button>
        </div>

        {/* Results Preview Card */}
        {result && (
          <div id="results-section" className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">Calculation Results</h3>
            
            {/* Total Demand */}
            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white shadow-lg">
              <p className="text-green-100 text-sm font-medium mb-1">Total Demand</p>
              <div className="flex items-end gap-3">
                <p className="text-4xl font-bold">
                  ${result.total_demand?.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) || '0'}
                </p>
                {baseline && (
                  <span className={`text-lg font-semibold px-2 py-0.5 rounded ${
                    calcPercentChange(result.total_demand || 0, baseline.totalDemand).pct >= 0 
                      ? 'bg-green-400/30 text-green-100' 
                      : 'bg-red-400/30 text-red-100'
                  }`}>
                    {calcPercentChange(result.total_demand || 0, baseline.totalDemand).formatted} vs conservative
                  </span>
                )}
              </div>
              <p className="text-green-200 text-sm mt-2">
                {useBestBase ? 'Best 36 Months' : 'Last 36 Months'} • {closureDuration} month closure
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Lost Profits */}
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white shadow-lg">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-4 h-4 text-blue-200" />
                  <p className="text-blue-100 text-sm font-medium">Lost Profits</p>
                </div>
                <p className="text-2xl font-bold">
                  ${result.pro_forma_summary?.total_lost_profits?.toLocaleString() || '0'}
                </p>
                {baseline && (
                  <p className={`text-sm font-medium mt-1 ${
                    calcPercentChange(result.pro_forma_summary?.total_lost_profits || 0, baseline.lostProfits).pct >= 0 
                      ? 'text-green-200' 
                      : 'text-red-200'
                  }`}>
                    {calcPercentChange(result.pro_forma_summary?.total_lost_profits || 0, baseline.lostProfits).formatted} from conservative
                  </p>
                )}
                <p className="text-xs text-blue-200 mt-1">{result.pro_forma_summary?.closure_months || closureDuration} months</p>
              </div>
              
              {/* Continuing Expenses */}
              <div className="bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl p-5 text-white shadow-lg">
                <div className="flex items-center gap-2 mb-1">
                  <DollarSign className="w-4 h-4 text-amber-200" />
                  <p className="text-amber-100 text-sm font-medium">Continuing Expenses</p>
                </div>
                <p className="text-2xl font-bold">
                  ${result.pro_forma_summary?.total_continuing_expenses?.toLocaleString() || '0'}
                </p>
                {baseline && (
                  <p className={`text-sm font-medium mt-1 ${
                    calcPercentChange(result.pro_forma_summary?.total_continuing_expenses || 0, baseline.continuingExpenses).pct >= 0 
                      ? 'text-green-200' 
                      : 'text-red-200'
                  }`}>
                    {calcPercentChange(result.pro_forma_summary?.total_continuing_expenses || 0, baseline.continuingExpenses).formatted} from conservative
                  </p>
                )}
              </div>
              
              {/* One-Time Costs */}
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-5 text-white shadow-lg">
                <div className="flex items-center gap-2 mb-1">
                  <Clock className="w-4 h-4 text-blue-200" />
                  <p className="text-blue-100 text-sm font-medium">One-Time Costs</p>
                </div>
                <p className="text-2xl font-bold">
                  ${result.one_time_costs?.total?.toLocaleString() || '0'}
                </p>
                {baseline && (
                  <p className={`text-sm font-medium mt-1 ${
                    calcPercentChange(result.one_time_costs?.total || 0, baseline.oneTimeCosts).pct >= 0 
                      ? 'text-green-200' 
                      : 'text-red-200'
                  }`}>
                    {calcPercentChange(result.one_time_costs?.total || 0, baseline.oneTimeCosts).formatted} from conservative
                  </p>
                )}
                <p className="text-xs text-blue-200 mt-1">Shutdown + Restart</p>
              </div>
            </div>
          </div>
        )}

        {/* Aggression Slider Card - NOW BELOW RESULTS */}
        {result && (
          <div id="aggression-section" className="bg-white rounded-xl border-2 border-blue-200 p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-100 via-yellow-100 to-red-100 rounded-lg flex items-center justify-center">
                <Target className="w-5 h-5 text-gray-700" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Fine-Tune Your Projection</h3>
                <p className="text-sm text-gray-500">Drag the slider to see results update in real-time</p>
              </div>
            </div>

            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-gray-600">Projection Stance</span>
              <div className="flex items-center space-x-2">
                <span className={`text-3xl font-bold ${getAggressionColor()}`}>
                  {aggression}
                </span>
                <span className={`text-sm font-medium ${getAggressionColor()}`}>
                  ({getAggressionLabel()})
                </span>
              </div>
            </div>
            
            <input
              type="range"
              min="0"
              max="100"
              value={aggression}
              onChange={(e) => setAggression(parseInt(e.target.value))}
              className="w-full h-3 rounded-lg appearance-none cursor-pointer"
              style={{
                background: `linear-gradient(to right, #3b82f6 0%, #eab308 50%, #ef4444 100%)`
              }}
            />
            
            <div className="flex justify-between text-xs text-gray-500 mt-2 mb-6">
              <span>Conservative (0)</span>
              <span>Moderate (50)</span>
              <span>Aggressive (100)</span>
            </div>

            {/* What the slider affects - DETAILED EXPLANATION */}
            <div className="bg-gray-50 rounded-lg p-4 space-y-3">
              <p className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                <Info className="w-4 h-4 text-blue-600" />
                What does this slider adjust?
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></div>
                  <div>
                    <span className="font-medium text-gray-800">Revenue Growth Rate</span>
                    <p className="text-gray-600 text-xs">
                      {aggression < 30 ? '−5% to 0% (decline expected)' : 
                       aggression < 70 ? '0% to 10% (modest growth)' : 
                       '10% to 18% (strong growth)'}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full mt-1.5 flex-shrink-0"></div>
                  <div>
                    <span className="font-medium text-gray-800">Gross Margin</span>
                    <p className="text-gray-600 text-xs">
                      {Math.round(55 + (aggression / 100) * 15)}% assumed profit margin
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-amber-500 rounded-full mt-1.5 flex-shrink-0"></div>
                  <div>
                    <span className="font-medium text-gray-800">Expense Continuation</span>
                    <p className="text-gray-600 text-xs">
                      {Math.round(10 + (aggression / 100) * 10)}% of revenue continues as fixed costs
                    </p>
                  </div>
                </div>
                
                <div className="flex items-start gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full mt-1.5 flex-shrink-0"></div>
                  <div>
                    <span className="font-medium text-gray-800">One-Time Costs</span>
                    <p className="text-gray-600 text-xs">
                      Shutdown: {Math.round(3 + (aggression / 100) * 4)}% • Restart: {Math.round(5 + (aggression / 100) * 10)}% of annual revenue
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="pt-2 border-t border-gray-200 mt-3">
                <p className="text-xs text-gray-500">
                  <strong>Conservative (0-30):</strong> Defensible in court, assumes economic headwinds<br/>
                  <strong>Moderate (30-70):</strong> Balanced projection based on historical trends<br/>
                  <strong>Aggressive (70-100):</strong> Maximum claim value, requires strong supporting evidence
                </p>
              </div>
            </div>
          </div>
        )}

      </div>
    </WizardV2Layout>
  );
}
